import { z } from 'zod';

/**
 * Wave 28 — Punch list de entrega.
 *
 * Final delivery walk-through items. Each row is something the PM +
 * owner flagged as pending before project handover can close out.
 *
 * Status lifecycle
 * ────────────────
 *   pendente     → freshly noted during the walk-through
 *   em_andamento → crew is addressing it
 *   concluido    → fixed and verified
 *   nao_aplicavel → reviewed and dismissed (e.g. owner withdrew ask)
 *
 * Priority vocabulary is deliberately coarse: a punch list does not
 * need an eight-step severity scale — three buckets map cleanly to
 * "block delivery / delay delivery / cosmetic touch-up".
 */

export const punchListStatusSchema = z.enum([
  'pendente',
  'em_andamento',
  'concluido',
  'nao_aplicavel',
]);
export type PunchListStatus = z.infer<typeof punchListStatusSchema>;

export const punchListPrioritySchema = z.enum(['baixa', 'normal', 'alta']);
export type PunchListPriority = z.infer<typeof punchListPrioritySchema>;

export const punchListItemSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  priority: punchListPrioritySchema,
  status: punchListStatusSchema,
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  attachmentId: z.string().nullable(),
  attachmentUrl: z.string().nullable(),
  reportedBy: z.string(),
  reportedByName: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PunchListItem = z.infer<typeof punchListItemSchema>;

export const createPunchListItemRequestSchema = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2000).optional(),
  location: z.string().trim().max(120).optional(),
  priority: punchListPrioritySchema.default('normal'),
  assigneeUserId: z.string().optional().nullable(),
  attachmentId: z.string().optional().nullable(),
});
export type CreatePunchListItemRequest = z.infer<
  typeof createPunchListItemRequestSchema
>;

export const updatePunchListItemRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(160).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    location: z.string().trim().max(120).nullable().optional(),
    priority: punchListPrioritySchema.optional(),
    status: punchListStatusSchema.optional(),
    assigneeUserId: z.string().nullable().optional(),
    attachmentId: z.string().nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
  });
export type UpdatePunchListItemRequest = z.infer<
  typeof updatePunchListItemRequestSchema
>;

/** Per-status counts + totals; used for the kanban header chips. */
export const punchListSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: z.object({
    pendente: z.number().int().nonnegative(),
    em_andamento: z.number().int().nonnegative(),
    concluido: z.number().int().nonnegative(),
    nao_aplicavel: z.number().int().nonnegative(),
  }),
  byPriority: z.object({
    baixa: z.number().int().nonnegative(),
    normal: z.number().int().nonnegative(),
    alta: z.number().int().nonnegative(),
  }),
  /** Readiness: 0-100 = concluido / (total - nao_aplicavel). */
  readinessPercent: z.number(),
});
export type PunchListSummary = z.infer<typeof punchListSummarySchema>;

export const listPunchListResponseSchema = z.object({
  items: z.array(punchListItemSchema),
  summary: punchListSummarySchema,
});
export type ListPunchListResponse = z.infer<typeof listPunchListResponseSchema>;
